import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../utils/apiClient';
import {
    PlusCircle,
    Edit,
    Trash2,
    Loader2,
    AlertTriangle,
    Save,
    X,
    CheckCircle2,
    Calendar,
    Clock,
    ShieldCheck,
    Sparkles,
    Layers,
    Users,
    UserCheck,
    Search,
    Filter,
    Info,
    Award,
    HeartHandshake,
    ArrowRight,
    Check,
    Ban
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// ============== ENUM NORMALİZASYON YARDIMCILARI ==============
// Backend (Program.cs) JsonStringEnumConverter kullandığı için enum alanları
// JSON'da string ("Yillik", "Gun", "Ucretli", "Aylik", "Role") veya sayı olarak gelebilir.
const normalizeKullanimBirimi = (val) => {
    if (val === null || val === undefined) return 1;
    if (typeof val === 'number') return [1, 2, 3].includes(val) ? val : 1;
    const s = String(val).trim().toLowerCase();
    if (s === '1' || s === 'gun' || s === 'gün') return 1;
    if (s === '2' || s === 'saat') return 2;
    if (s === '3' || s === 'esnek') return 3;
    return 1;
};

const normalizeOdemeDurumu = (val) => {
    if (val === null || val === undefined) return 1;
    if (typeof val === 'number') return val === 2 ? 2 : 1;
    const s = String(val).trim().toLowerCase();
    if (s === '2' || s === 'ucretsiz' || s === 'ücretsiz') return 2;
    return 1;
};

const normalizeBakiyeModu = (val) => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return [0, 1, 2].includes(val) ? val : 0;
    const s = String(val).trim().toLowerCase();
    if (s === '1' || s === 'yillik' || s === 'yıllık') return 1;
    if (s === '2' || s === 'eventlimit' || s === 'event_limit') return 2;
    return 0;
};

const normalizeEventPeriyodu = (val) => {
    if (val === null || val === undefined || val === '') return null;
    if (typeof val === 'number') return [1, 2, 3].includes(val) ? val : null;
    const s = String(val).trim().toLowerCase();
    if (s === '1' || s === 'aylik' || s === 'aylık') return 1;
    if (s === '2' || s === 'yillik' || s === 'yıllık') return 2;
    if (s === '3' || s === 'tekseferlik' || s === 'tek_seferlik') return 3;
    return null;
};

const normalizeOnayciTipi = (val) => {
    if (val === null || val === undefined) return 1;
    if (typeof val === 'number') return val === 2 ? 2 : 1;
    const s = String(val).trim().toLowerCase();
    if (s === '2' || s === 'user') return 2;
    return 1;
};

const normalizeLeaveTypeItem = (lt) => {
    if (!lt) return lt;
    const bakiyeModu = normalizeBakiyeModu(lt.bakiyeModu);
    return {
        ...lt,
        kullanimBirimi: normalizeKullanimBirimi(lt.kullanimBirimi),
        odemeDurumu: normalizeOdemeDurumu(lt.odemeDurumu),
        bakiyeModu,
        bakiyedenDusulsun: bakiyeModu > 0,
        isAnaYillikIzin: bakiyeModu === 1 ? Boolean(lt.isAnaYillikIzin) : false,
        eventPeriyodu: normalizeEventPeriyodu(lt.eventPeriyodu),
        eventLimitBirimi: Number(lt.eventLimitBirimi) === 2 ? 2 : 1,
        onayAdimlari: Array.isArray(lt.onayAdimlari)
            ? lt.onayAdimlari.map((a, idx) => ({
                  ...a,
                  sira: Number(a.sira ?? idx + 1),
                  onayciTipi: normalizeOnayciTipi(a.onayciTipi),
                  herhangiBiriOnaylayabilir: Boolean(a.herhangiBiriOnaylayabilir ?? true),
                  roleIds: Array.isArray(a.roleIds) ? a.roleIds.map((x) => Number(x)) : [],
                  userIds: Array.isArray(a.userIds) ? a.userIds.map((x) => Number(x)) : [],
              }))
            : [],
        kidemKurallari: Array.isArray(lt.kidemKurallari) ? lt.kidemKurallari : [],
        yasKurallari: Array.isArray(lt.yasKurallari) ? lt.yasKurallari : [],
    };
};

// ============== ENUM & ETİKET HARİTALARI ==============
const KULLANIM_BIRIMI_MAP = {
    1: { label: 'Günlük', desc: 'Tam gün bazlı izin kullanımı', badge: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60' },
    2: { label: 'Saatlik', desc: 'Saat ve dakika bazlı izin kullanımı', badge: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800/60' },
    3: { label: 'Esnek (Gün / Saat)', desc: 'Talep sırasında günlük veya saatlik seçilebilir', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800/60' },
};

const ODEME_DURUMU_MAP = {
    1: { label: 'Ücretli İzin', desc: 'Maaş ve puantaj kesintisi yapılmaz', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/60' },
    2: { label: 'Ücretsiz İzin', desc: 'Çalışılmayan süre puantajdan düşülür', badge: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/60' },
};

const BAKIYE_MODU_MAP = {
    0: { label: 'Bakiyesiz (Limitsiz)', short: 'Bakiyesiz', badge: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700' },
    1: { label: 'Yıllık Hak Ediş', short: 'Yıllık Hak Ediş', badge: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60' },
    2: { label: 'Dönemsel / Olay Bazlı Limit', short: 'Dönemsel / Olay Limitli', badge: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800/60' },
};

const DONEMSEL_PERIYOT_MAP = {
    1: 'Aylık Kota',
    2: 'Yıllık Kota',
    3: 'Olay Başına (Tek Seferlik)',
};

// ============== ANA BİLEŞEN ==============
function LeaveTypeManagement() {
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [formError, setFormError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showForm, setShowForm] = useState(false);
    const [editingLeaveType, setEditingLeaveType] = useState(null);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [deletingLeaveType, setDeletingLeaveType] = useState(null);

    const [showUsageWarningModal, setShowUsageWarningModal] = useState(false);
    const [warningModalData, setWarningModalData] = useState({ leaveType: null, usageDetails: [] });

    // Filtre & Arama स्टेटleri
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL'); // ALL, ACTIVE, PASSIVE
    const [modeFilter, setModeFilter] = useState('ALL'); // ALL, 0, 1, 2
    const [unitFilter, setUnitFilter] = useState('ALL'); // ALL, 1, 2, 3

    const { hasPermission } = useAuth();
    const canCreate = hasPermission('leave_types:create');
    const canUpdate = hasPermission('leave_types:update');
    const canDelete = hasPermission('leave_types:delete');

    // İzin türlerini çek
    const fetchLeaveTypes = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await apiClient('izinturu');
            setLeaveTypes(Array.isArray(data) ? data.map(normalizeLeaveTypeItem) : []);
        } catch (err) {
            setError(err?.userFriendlyMessage || err?.message || 'İzin türleri yüklenirken bir hata oluştu.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLeaveTypes();
    }, [fetchLeaveTypes]);

    // Özet KPI İstatistikleri
    const stats = useMemo(() => {
        const total = leaveTypes.length;
        const active = leaveTypes.filter((x) => x.isActive).length;
        const passive = total - active;
        const annualMode = leaveTypes.filter((x) => Number(x.bakiyeModu) === 1).length;
        const periodicMode = leaveTypes.filter((x) => Number(x.bakiyeModu) === 2).length;
        const withApproval = leaveTypes.filter((x) => x.onayGereklidir).length;
        const primaryAnnual = leaveTypes.find((x) => x.isActive && x.isAnaYillikIzin && Number(x.bakiyeModu) === 1) || null;
        return { total, active, passive, annualMode, periodicMode, withApproval, primaryAnnual };
    }, [leaveTypes]);

    // Filtrelenmiş Liste
    const filteredLeaveTypes = useMemo(() => {
        return leaveTypes.filter((lt) => {
            const q = searchTerm.trim().toLowerCase();
            if (q) {
                const matchKod = (lt.kod || '').toLowerCase().includes(q);
                const matchAd = (lt.ad || '').toLowerCase().includes(q);
                if (!matchKod && !matchAd) return false;
            }
            if (statusFilter === 'ACTIVE' && !lt.isActive) return false;
            if (statusFilter === 'PASSIVE' && lt.isActive) return false;
            if (modeFilter !== 'ALL' && Number(lt.bakiyeModu ?? 0) !== Number(modeFilter)) return false;
            if (unitFilter !== 'ALL' && Number(lt.kullanimBirimi ?? 1) !== Number(unitFilter)) return false;
            return true;
        });
    }, [leaveTypes, searchTerm, statusFilter, modeFilter, unitFilter]);

    // Form kaydet (Ekle / Güncelle)
    const handleSave = async (Payload) => {
        setIsSubmitting(true);
        setFormError('');
        try {
            if (editingLeaveType && editingLeaveType.id) {
                await apiClient(`izinturu/${editingLeaveType.id}`, {
                    method: 'PUT',
                    body: Payload,
                });
            } else {
                await apiClient('izinturu', {
                    method: 'POST',
                    body: Payload,
                });
            }
            setShowForm(false);
            setEditingLeaveType(null);
            await fetchLeaveTypes();
        } catch (err) {
            let errorMessage = err?.userFriendlyMessage || err?.data?.message || err?.message || 'İşlem sırasında bir hata oluştu.';
            const data = err?.data || err?.response?.data;
            if (data) {
                if (data.message) {
                    errorMessage = data.message;
                } else if (data.errors) {
                    errorMessage = Object.values(data.errors).flat().join(' ');
                } else if (typeof data === 'string') {
                    errorMessage = data;
                }
            }
            setFormError(errorMessage);
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Aktif / Pasif toggle
    const handleToggleStatus = async (leaveType) => {
        const newStatus = !leaveType.isActive;

        // Aktif -> Pasif yapılıyorsa kullanım kontrolü
        if (newStatus === false) {
            try {
                const data = await apiClient(`izinturu/${leaveType.id}/kullanim`);
                if (data && data.isUsed) {
                    setWarningModalData({
                        leaveType: leaveType,
                        usageDetails: data.usageDetails || []
                    });
                    setShowUsageWarningModal(true);
                } else {
                    await proceedWithStatusChange(leaveType, false);
                }
            } catch (err) {
                setError(err?.userFriendlyMessage || err?.message || 'İzin türü kullanım durumu kontrol edilirken bir hata oluştu.');
            }
        } else {
            await proceedWithStatusChange(leaveType, true);
        }
    };

    const proceedWithStatusChange = async (leaveType, newStatus) => {
        try {
            setError('');
            await apiClient(`izinturu/${leaveType.id}/durum`, {
                method: 'PUT',
                body: JSON.stringify(newStatus),
                headers: { 'Content-Type': 'application/json' }
            });
            await fetchLeaveTypes();
        } catch (err) {
            setError(err?.userFriendlyMessage || err?.data?.message || err?.message || 'Durum güncellenirken bir hata oluştu.');
        } finally {
            setShowUsageWarningModal(false);
            setWarningModalData({ leaveType: null, usageDetails: [] });
        }
    };

    // Silme
    const handleDeleteRequest = (leaveType) => {
        setDeletingLeaveType(leaveType);
        setShowConfirmModal(true);
    };

    const confirmDelete = async () => {
        if (!deletingLeaveType) return;
        try {
            setError('');
            await apiClient(`izinturu/${deletingLeaveType.id}`, {
                method: 'DELETE',
            });
            setShowConfirmModal(false);
            setDeletingLeaveType(null);
            await fetchLeaveTypes();
        } catch (err) {
            setError(err?.userFriendlyMessage || err?.data?.message || err?.message || 'Silme işlemi sırasında bir hata oluştu.');
            setShowConfirmModal(false);
            setDeletingLeaveType(null);
        }
    };

    // Hızlı Ana Yıllık İzin Şablonu Açma
    const handleOpenQuickAnnualLeaveTemplate = () => {
        setEditingLeaveType({
            id: null,
            kod: 'YIL',
            ad: 'Yıllık Ücretli İzin',
            isActive: true,
            kullanimBirimi: 1,
            odemeDurumu: 1,
            bakiyedenDusulsun: true,
            varsayilanGunSaat: 8,
            bakiyeModu: 1,
            isAnaYillikIzin: true,
            yillikDevredilsinMi: true,
            yillikDevredenSilmeYili: null,
            kidemKuralKullanilsinMi: true,
            yasKuralKullanilsinMi: true,
            kidemKurallari: [
                { id: null, minKidemYil: 1, maxKidemYil: 5, gunSayisi: 14 },
                { id: null, minKidemYil: 6, maxKidemYil: 14, gunSayisi: 20 },
                { id: null, minKidemYil: 15, maxKidemYil: null, gunSayisi: 26 },
            ],
            yasKurallari: [
                { id: null, minYas: 0, maxYas: 18, gunSayisi: 20 },
                { id: null, minYas: 50, maxYas: null, gunSayisi: 20 },
            ],
            negatifBakiyeKullanilsinMi: false,
            negatifBakiyeGunLimiti: null,
            resmiTatillerBakiyedenDusulsunMu: false,
            haftaTatilleriBakiyedenDusulsunMu: false,
            onayGereklidir: false,
            adminOnaysizGirebilirMi: true,
            onayAdimlari: [],
        });
        setFormError('');
        setShowForm(true);
    };

    return (
        <div className="space-y-6">
            {/* Üst Bilgi & Aksiyon Çubuğu (Sayfa başlığı üst barda yer aldığı için tekrar edilmez) */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm">
                <div className="flex items-start gap-3.5">
                    <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-950/50 border border-blue-100 dark:border-blue-900/60 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
                        <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                            Kurumsal İzin Politikaları, Hak Ediş ve Onay Akışı Yapılandırması
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            Yıllık izin kıdem/yaş tablolarını, dönemsel/olay bazlı izin limitlerini, puantaj kesinti kurallarını ve çok adımlı onay süreçlerini buradan yönetebilirsiniz.
                        </p>
                    </div>
                </div>

                {canCreate && (
                    <button
                        onClick={() => {
                            setEditingLeaveType(null);
                            setFormError('');
                            setShowForm(true);
                        }}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-all shrink-0"
                    >
                        <PlusCircle className="w-4 h-4" />
                        Yeni İzin Türü Tanımla
                    </button>
                )}
            </div>

            {/* KPI Özet Kartları */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Toplam İzin Türü</p>
                        <div className="flex items-baseline gap-2 mt-1">
                            <span className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</span>
                            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                                {stats.active} Aktif
                            </span>
                            {stats.passive > 0 && (
                                <span className="text-xs text-slate-400">• {stats.passive} Pasif</span>
                            )}
                        </div>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300">
                        <Layers className="w-5 h-5" />
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Yıllık Hak Edişli Türler</p>
                        <div className="flex items-baseline gap-2 mt-1">
                            <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.annualMode}</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">Kıdem / Yaş bazlı</span>
                        </div>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center text-blue-600 dark:text-blue-400">
                        <Award className="w-5 h-5" />
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Dönemsel / Olay Limitli</p>
                        <div className="flex items-baseline gap-2 mt-1">
                            <span className="text-2xl font-bold text-violet-600 dark:text-violet-400">{stats.periodicMode}</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">Mazeret, Evlilik vb.</span>
                        </div>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-violet-50 dark:bg-violet-950/40 flex items-center justify-center text-violet-600 dark:text-violet-400">
                        <HeartHandshake className="w-5 h-5" />
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Onay Akışına Tabi</p>
                        <div className="flex items-baseline gap-2 mt-1">
                            <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.withApproval}</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">Kademeli onay</span>
                        </div>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                        <ShieldCheck className="w-5 h-5" />
                    </div>
                </div>
            </div>

            {/* Ana Yıllık İzin Bilgilendirme / Hızlı Kurulum Uyarısı */}
            {!loading && !stats.primaryAnnual && (
                <div className="p-4 rounded-2xl bg-amber-50/90 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0 mt-0.5">
                            <Award className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-amber-900 dark:text-amber-200">
                                Firmanın Ana (Resmi) Yıllık İzin Türü Henüz Belirlenmemiş
                            </p>
                            <p className="text-xs text-amber-800/90 dark:text-amber-300/80 mt-0.5">
                                Yıllık hak edişlerin ve &ldquo;Limit aşılırsa yıllık izinden düşülsün&rdquo; kuralının isimlendirmeden bağımsız olarak hatasız çalışması için bir izin türünü <strong>Ana Yıllık İzin</strong> olarak işaretleyin veya hazır şablonla oluşturun.
                            </p>
                        </div>
                    </div>
                    {canCreate && (
                        <button
                            type="button"
                            onClick={handleOpenQuickAnnualLeaveTemplate}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white shadow-sm transition-colors shrink-0"
                        >
                            <Sparkles className="w-4 h-4" />
                            4857 İş Kanunu Ana Yıllık İzni Oluştur
                        </button>
                    )}
                </div>
            )}

            {/* Hata Mesajı */}
            {error && (
                <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/60 flex items-center justify-between gap-3 text-red-700 dark:text-red-300 text-sm">
                    <div className="flex items-center gap-2.5">
                        <AlertTriangle className="w-5 h-5 shrink-0" />
                        <span>{error}</span>
                    </div>
                    <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Arama & Filtre Çubuğu */}
            <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col md:flex-row items-stretch md:items-center gap-3">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="İzin kodu veya izin türü adına göre ara..."
                        className="w-full pl-10 pr-4 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    />
                </div>

                <div className="flex flex-wrap items-center gap-2.5">
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                        <Filter className="w-3.5 h-3.5" />
                        <span>Filtrele:</span>
                    </div>

                    <select
                        value={modeFilter}
                        onChange={(e) => setModeFilter(e.target.value)}
                        className="px-3 py-2 text-xs font-medium rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    >
                        <option value="ALL">Tüm Bakiye Modelleri</option>
                        <option value="1">Yıllık Hak Ediş</option>
                        <option value="2">Dönemsel / Olay Limitli</option>
                        <option value="0">Bakiyesiz (Limitsiz)</option>
                    </select>

                    <select
                        value={unitFilter}
                        onChange={(e) => setUnitFilter(e.target.value)}
                        className="px-3 py-2 text-xs font-medium rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    >
                        <option value="ALL">Tüm Kullanım Birimleri</option>
                        <option value="1">Günlük</option>
                        <option value="2">Saatlik</option>
                        <option value="3">Esnek (Gün / Saat)</option>
                    </select>

                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 text-xs font-medium rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    >
                        <option value="ALL">Tüm Durumlar</option>
                        <option value="ACTIVE">Sadece Aktifler</option>
                        <option value="PASSIVE">Sadece Pasifler</option>
                    </select>
                </div>
            </div>

            {/* İzin Türleri Tablosu */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm overflow-hidden">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                        <p className="text-sm text-slate-500 dark:text-slate-400">İzin türleri yükleniyor...</p>
                    </div>
                ) : filteredLeaveTypes.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                        <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 mb-3">
                            <Calendar className="w-6 h-6" />
                        </div>
                        <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                            Kayıtlı İzin Türü Bulunamadı
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-md">
                            Arama veya filtre kriterlerinize uygun izin türü bulunamadı ya da henüz yeni bir izin türü tanımlanmadı.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200/80 dark:divide-slate-800">
                            <thead className="bg-slate-50/80 dark:bg-slate-800/60">
                                <tr>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Kod & İzin Türü
                                    </th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Kullanım & Süre
                                    </th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Ödeme Durumu
                                    </th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Bakiye & Hak Ediş Modeli
                                    </th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Onay & Tatil Kuralı
                                    </th>
                                    <th className="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Durum
                                    </th>
                                    {(canUpdate || canDelete) && (
                                        <th className="px-5 py-3.5 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                            İşlemler
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200/60 dark:divide-slate-800/70">
                                {filteredLeaveTypes.map((lt) => {
                                    const unitInfo = KULLANIM_BIRIMI_MAP[lt.kullanimBirimi] || KULLANIM_BIRIMI_MAP[1];
                                    const payInfo = ODEME_DURUMU_MAP[lt.odemeDurumu] || ODEME_DURUMU_MAP[1];
                                    const modeInfo = BAKIYE_MODU_MAP[lt.bakiyeModu ?? 0] || BAKIYE_MODU_MAP[0];
                                    const stepCount = Array.isArray(lt.onayAdimlari) ? lt.onayAdimlari.length : 0;

                                    return (
                                        <tr
                                            key={lt.id}
                                            className={`transition-colors ${
                                                lt.isActive
                                                    ? 'hover:bg-slate-50/80 dark:hover:bg-slate-800/40'
                                                    : 'bg-slate-50/40 dark:bg-slate-900/40 opacity-65'
                                            }`}
                                        >
                                            {/* Kod & Ad */}
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-3">
                                                    <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-lg text-xs font-mono font-bold bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200/80 dark:border-slate-700">
                                                        {lt.kod}
                                                    </span>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                                {lt.ad}
                                                            </span>
                                                            {lt.isAnaYillikIzin && lt.isActive && Number(lt.bakiyeModu) === 1 && (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700/60">
                                                                    ★ Ana Yıllık İzin
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-slate-500 dark:text-slate-400">
                                                            {lt.bakiyedenDusulsun ? 'Bakiyeden düşülür' : 'Bakiye düşümü yapılmaz'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Kullanım Birimi & Günlük Saat */}
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="flex flex-col items-start gap-1">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium border ${unitInfo.badge}`}>
                                                        {unitInfo.label}
                                                    </span>
                                                    <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {lt.varsayilanGunSaat
                                                            ? `1 Gün = ${lt.varsayilanGunSaat} Saat`
                                                            : 'Vardiya süresine göre'}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Ödeme Durumu */}
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${payInfo.badge}`}>
                                                    {payInfo.label}
                                                </span>
                                            </td>

                                            {/* Bakiye & Hak Ediş Modeli */}
                                            <td className="px-5 py-4">
                                                <div className="flex flex-col items-start gap-1">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium border ${modeInfo.badge}`}>
                                                        {modeInfo.short}
                                                    </span>

                                                    {Number(lt.bakiyeModu) === 1 && (
                                                        <div className="text-xs text-slate-500 dark:text-slate-400 flex flex-wrap gap-1.5 mt-0.5">
                                                            {lt.kidemKuralKullanilsinMi && (
                                                                <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                                                    Kıdem ({lt.kidemKurallari?.length || 0} kural)
                                                                </span>
                                                            )}
                                                            {lt.yasKuralKullanilsinMi && (
                                                                <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                                                    Yaş ({lt.yasKurallari?.length || 0} kural)
                                                                </span>
                                                            )}
                                                            {lt.yillikDevredilsinMi && (
                                                                <span className="px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400">
                                                                    Devredilebilir
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {Number(lt.bakiyeModu) === 2 && (
                                                        <div className="text-xs text-slate-500 dark:text-slate-400">
                                                            {DONEMSEL_PERIYOT_MAP[lt.eventPeriyodu] || 'Dönemsel'}:{' '}
                                                            <strong className="text-slate-700 dark:text-slate-200">
                                                                {lt.eventLimitToplamGun ?? 0}{' '}
                                                                {Number(lt.eventLimitBirimi) === 2 ? 'Saat' : 'Gün'}
                                                            </strong>
                                                            {lt.eventLimitAsimiYillikIzindenDusulsunMu && (
                                                                <span className="ml-1 text-violet-600 dark:text-violet-400">
                                                                    (Aşım:{' '}
                                                                    {leaveTypes.find((x) => Number(x.id) === Number(lt.eventLimitAsimiHedefIzinTuruId))?.ad ||
                                                                        stats.primaryAnnual?.ad ||
                                                                        'Yıllık İzin'}
                                                                    )
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {lt.negatifBakiyeKullanilsinMi && (
                                                        <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
                                                            Avans / Eksi Bakiye:{' '}
                                                            {lt.negatifBakiyeGunLimiti
                                                                ? `En fazla ${lt.negatifBakiyeGunLimiti} gün`
                                                                : 'Sınırsız'}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Onay & Tatil Kuralı */}
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="flex flex-col items-start gap-1">
                                                    {lt.onayGereklidir ? (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/60">
                                                            <ShieldCheck className="w-3.5 h-3.5" />
                                                            {stepCount > 0 ? `${stepCount} Adımlı Onay` : 'Onay Süreci Aktif'}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700">
                                                            Onaysız Direkt Kayıt
                                                        </span>
                                                    )}

                                                    <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-2">
                                                        <span>
                                                            HT: {lt.haftaTatilleriBakiyedenDusulsunMu ? 'Düşülür' : 'Düşülmez'}
                                                        </span>
                                                        <span>•</span>
                                                        <span>
                                                            RT: {lt.resmiTatillerBakiyedenDusulsunMu ? 'Düşülür' : 'Düşülmez'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Durum Toggle */}
                                            <td className="px-5 py-4 whitespace-nowrap text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => canUpdate && handleToggleStatus(lt)}
                                                    disabled={!canUpdate}
                                                    title={
                                                        canUpdate
                                                            ? lt.isActive
                                                                ? 'Pasif yapmak için tıklayın'
                                                                : 'Aktif yapmak için tıklayın'
                                                            : 'Yetkiniz yok'
                                                    }
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                                        lt.isActive
                                                            ? 'bg-emerald-500'
                                                            : 'bg-slate-300 dark:bg-slate-700'
                                                    } ${!canUpdate ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                >
                                                    <span
                                                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                                            lt.isActive ? 'translate-x-6' : 'translate-x-1'
                                                        }`}
                                                    />
                                                </button>
                                            </td>

                                            {/* İşlemler */}
                                            {(canUpdate || canDelete) && (
                                                <td className="px-5 py-4 whitespace-nowrap text-right">
                                                    <div className="inline-flex items-center justify-end gap-1.5">
                                                        {canUpdate && (
                                                            <button
                                                                onClick={() => {
                                                                    setEditingLeaveType(lt);
                                                                    setFormError('');
                                                                    setShowForm(true);
                                                                }}
                                                                className="p-2 rounded-xl text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/50 transition-colors"
                                                                title="İzin Türünü Düzenle"
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                onClick={() => handleDeleteRequest(lt)}
                                                                className="p-2 rounded-xl text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/50 transition-colors"
                                                                title="İzin Türünü Sil"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Oluşturma / Düzenleme Modalı */}
            {showForm && (
                <LeaveTypeFormModal
                    initialData={editingLeaveType}
                    allLeaveTypes={leaveTypes}
                    onSave={handleSave}
                    onCancel={() => {
                        setShowForm(false);
                        setEditingLeaveType(null);
                        setFormError('');
                    }}
                    apiError={formError}
                    isSubmitting={isSubmitting}
                />
            )}

            {/* Silme Onay Modalı */}
            <ConfirmationModal
                show={showConfirmModal}
                title="İzin Türünü Silmeyi Onayla"
                onConfirm={confirmDelete}
                onCancel={() => {
                    setShowConfirmModal(false);
                    setDeletingLeaveType(null);
                }}
            >
                <p className="text-sm text-slate-600 dark:text-slate-300">
                    <strong className="font-semibold text-slate-900 dark:text-white">
                        {deletingLeaveType?.ad} ({deletingLeaveType?.kod})
                    </strong>{' '}
                    adlı izin türünü silmek istediğinizden emin misiniz? Bu izin türü geçmişte herhangi bir izin talebinde kullanıldıysa veri bütünlüğü için silinemez, bunun yerine pasife alabilirsiniz.
                </p>
            </ConfirmationModal>

            {/* Kullanım Uyarısı Modalı */}
            <UsageWarningModal
                show={showUsageWarningModal}
                leaveType={warningModalData.leaveType}
                usageDetails={warningModalData.usageDetails}
                onConfirm={() => proceedWithStatusChange(warningModalData.leaveType, false)}
                onCancel={() => {
                    setShowUsageWarningModal(false);
                    setWarningModalData({ leaveType: null, usageDetails: [] });
                }}
            />
        </div>
    );
}

// ============== FORM MODALI (YENİ EKLE / DÜZENLE) ==============
const LeaveTypeFormModal = ({ initialData, allLeaveTypes = [], onSave, onCancel, apiError, isSubmitting }) => {
    // Sistemdeki mevcut Yıllık Hak Edişli izin türleri (aşımın düşüleceği hedef seçimi için)
    const annualLeaveOptions = useMemo(() => {
        return (Array.isArray(allLeaveTypes) ? allLeaveTypes : [])
            .filter((lt) => normalizeBakiyeModu(lt.bakiyeModu) === 1 && (!initialData?.id || Number(lt.id) !== Number(initialData.id)))
            .sort((a, b) => Number(Boolean(b.isAnaYillikIzin)) - Number(Boolean(a.isAnaYillikIzin)));
    }, [allLeaveTypes, initialData]);

    // Sistemde halihazırda başka bir AKTİF Ana Yıllık İzin var mı? (Varsa ikinci bir ana izin seçimine izin verilmez)
    const existingActivePrimaryAnnual = useMemo(() => {
        return (
            (Array.isArray(allLeaveTypes) ? allLeaveTypes : []).find(
                (lt) =>
                    lt.isActive &&
                    Boolean(lt.isAnaYillikIzin) &&
                    normalizeBakiyeModu(lt.bakiyeModu) === 1 &&
                    (!initialData?.id || Number(lt.id) !== Number(initialData.id))
            ) || null
        );
    }, [allLeaveTypes, initialData]);

    const defaultTargetAnnualId = useMemo(() => {
        const primary = annualLeaveOptions.find((lt) => lt.isActive && lt.isAnaYillikIzin) || annualLeaveOptions.find((lt) => lt.isAnaYillikIzin);
        if (primary) return String(primary.id);
        if (annualLeaveOptions.length > 0) return String(annualLeaveOptions[0].id);
        return '';
    }, [annualLeaveOptions]);

    const buildFormState = useCallback(
        (src) => {
            if (!src) {
                return {
                    kod: '',
                    ad: '',
                    isActive: true,
                    kullanimBirimi: 1,
                    odemeDurumu: 1,
                    bakiyedenDusulsun: false,
                    varsayilanGunSaat: '8',

                    bakiyeModu: 0, // 0 = Bakiyesiz, 1 = Yıllık Hak Ediş, 2 = Dönemsel / Olay Bazlı Limit
                    isAnaYillikIzin: false,
                    yillikDevredilsinMi: true,
                    yillikDevredenSilmeYili: '',
                    kidemKuralKullanilsinMi: false,
                    yasKuralKullanilsinMi: false,
                    eventPeriyodu: '', // 1 = Aylık, 2 = Yıllık, 3 = Tek Seferlik
                    eventLimitBirimi: 1, // 1 = Gün, 2 = Saat
                    eventLimitToplamGun: '',
                    eventLimitAsimiYillikIzindenDusulsunMu: false,
                    eventLimitAsimiHedefIzinTuruId: defaultTargetAnnualId,

                    negatifBakiyeKullanilsinMi: false,
                    negatifBakiyeGunLimiti: '',

                    resmiTatillerBakiyedenDusulsunMu: false,
                    haftaTatilleriBakiyedenDusulsunMu: false,

                    onayGereklidir: false,
                    adminOnaysizGirebilirMi: false,
                };
            }

            const normBakiyeModu = normalizeBakiyeModu(src.bakiyeModu);
            const normEventPeriyodu = normalizeEventPeriyodu(src.eventPeriyodu);

            return {
                kod: src.kod || '',
                ad: src.ad || '',
                isActive: src.isActive ?? true,
                kullanimBirimi: normalizeKullanimBirimi(src.kullanimBirimi),
                odemeDurumu: normalizeOdemeDurumu(src.odemeDurumu),
                bakiyedenDusulsun: normBakiyeModu > 0,
                varsayilanGunSaat:
                    src.varsayilanGunSaat !== null && src.varsayilanGunSaat !== undefined
                        ? String(src.varsayilanGunSaat)
                        : '',

                bakiyeModu: normBakiyeModu,
                isAnaYillikIzin: normBakiyeModu === 1 ? Boolean(src.isAnaYillikIzin ?? false) : false,
                yillikDevredilsinMi: src.yillikDevredilsinMi ?? true,
                yillikDevredenSilmeYili:
                    src.yillikDevredenSilmeYili !== null && src.yillikDevredenSilmeYili !== undefined
                        ? String(src.yillikDevredenSilmeYili)
                        : '',
                kidemKuralKullanilsinMi: Boolean(src.kidemKuralKullanilsinMi ?? false),
                yasKuralKullanilsinMi: Boolean(src.yasKuralKullanilsinMi ?? false),
                eventPeriyodu: normEventPeriyodu !== null ? String(normEventPeriyodu) : '',
                eventLimitBirimi: Number(src.eventLimitBirimi) === 2 ? 2 : 1,
                eventLimitToplamGun:
                    src.eventLimitToplamGun !== null && src.eventLimitToplamGun !== undefined
                        ? String(src.eventLimitToplamGun)
                        : '',
                eventLimitAsimiYillikIzindenDusulsunMu: Boolean(src.eventLimitAsimiYillikIzindenDusulsunMu ?? false),
                eventLimitAsimiHedefIzinTuruId:
                    src.eventLimitAsimiHedefIzinTuruId !== null && src.eventLimitAsimiHedefIzinTuruId !== undefined
                        ? String(src.eventLimitAsimiHedefIzinTuruId)
                        : defaultTargetAnnualId,

                negatifBakiyeKullanilsinMi: Boolean(src.negatifBakiyeKullanilsinMi ?? false),
                negatifBakiyeGunLimiti:
                    src.negatifBakiyeGunLimiti !== null && src.negatifBakiyeGunLimiti !== undefined
                        ? String(src.negatifBakiyeGunLimiti)
                        : '',

                resmiTatillerBakiyedenDusulsunMu: Boolean(src.resmiTatillerBakiyedenDusulsunMu ?? false),
                haftaTatilleriBakiyedenDusulsunMu: Boolean(src.haftaTatilleriBakiyedenDusulsunMu ?? false),

                onayGereklidir: Boolean(src.onayGereklidir ?? false),
                adminOnaysizGirebilirMi: Boolean(src.adminOnaysizGirebilirMi ?? false),
            };
        },
        [defaultTargetAnnualId]
    );

    const [formData, setFormData] = useState(() => buildFormState(initialData));

    const [kidemRules, setKidemRules] = useState(() =>
        Array.isArray(initialData?.kidemKurallari)
            ? initialData.kidemKurallari.map((k) => ({
                  id: k.id,
                  minKidemYil: k.minKidemYil ?? '',
                  maxKidemYil: k.maxKidemYil !== null && k.maxKidemYil !== undefined ? k.maxKidemYil : '',
                  gunSayisi: k.gunSayisi ?? '',
              }))
            : []
    );

    const [yasRules, setYasRules] = useState(() =>
        Array.isArray(initialData?.yasKurallari)
            ? initialData.yasKurallari.map((y) => ({
                  id: y.id,
                  minYas: y.minYas ?? '',
                  maxYas: y.maxYas !== null && y.maxYas !== undefined ? y.maxYas : '',
                  gunSayisi: y.gunSayisi ?? '',
              }))
            : []
    );

    const [rolesForDropdown, setRolesForDropdown] = useState([]);
    const [employeesForDropdown, setEmployeesForDropdown] = useState([]);
    const [approvalSteps, setApprovalSteps] = useState(() =>
        Array.isArray(initialData?.onayAdimlari)
            ? initialData.onayAdimlari
                  .slice()
                  .sort((a, b) => (a.sira ?? 0) - (b.sira ?? 0))
                  .map((s, idx) => ({
                      sira: idx + 1,
                      onayciTipi: normalizeOnayciTipi(s.onayciTipi),
                      herhangiBiriOnaylayabilir: Boolean(s.herhangiBiriOnaylayabilir ?? true),
                      roleIds: Array.isArray(s.roleIds) ? s.roleIds.map((x) => Number(x)) : [],
                      userIds: Array.isArray(s.userIds) ? s.userIds.map((x) => Number(x)) : [],
                  }))
            : []
    );
    const [approvalError, setApprovalError] = useState('');
    const [localValidationError, setLocalValidationError] = useState('');
    const [stepSearchTerms, setStepSearchTerms] = useState({});

    const isEditing = Boolean(initialData && initialData.id);

    // initialData değiştiğinde formu senkronize et
    useEffect(() => {
        setFormData(buildFormState(initialData));

        setKidemRules(
            Array.isArray(initialData?.kidemKurallari)
                ? initialData.kidemKurallari.map((k) => ({
                      id: k.id,
                      minKidemYil: k.minKidemYil ?? '',
                      maxKidemYil: k.maxKidemYil !== null && k.maxKidemYil !== undefined ? k.maxKidemYil : '',
                      gunSayisi: k.gunSayisi ?? '',
                  }))
                : []
        );

        setYasRules(
            Array.isArray(initialData?.yasKurallari)
                ? initialData.yasKurallari.map((y) => ({
                      id: y.id,
                      minYas: y.minYas ?? '',
                      maxYas: y.maxYas !== null && y.maxYas !== undefined ? y.maxYas : '',
                      gunSayisi: y.gunSayisi ?? '',
                  }))
                : []
        );

        setApprovalSteps(
            Array.isArray(initialData?.onayAdimlari)
                ? initialData.onayAdimlari
                      .slice()
                      .sort((a, b) => (a.sira ?? 0) - (b.sira ?? 0))
                      .map((s, idx) => ({
                          sira: idx + 1,
                          onayciTipi: normalizeOnayciTipi(s.onayciTipi),
                          herhangiBiriOnaylayabilir: Boolean(s.herhangiBiriOnaylayabilir ?? true),
                          roleIds: Array.isArray(s.roleIds) ? s.roleIds.map((x) => Number(x)) : [],
                          userIds: Array.isArray(s.userIds) ? s.userIds.map((x) => Number(x)) : [],
                      }))
                : []
        );
    }, [initialData, buildFormState]);

    // Rol ve personel dropdown listesini çek
    useEffect(() => {
        let alive = true;
        const loadLookups = async () => {
            try {
                const [rolesRes, empRes] = await Promise.all([
                    apiClient('roles/dropdown').catch(() => []),
                    apiClient('employee/dropdown').catch(() => []),
                ]);
                if (!alive) return;
                setRolesForDropdown(Array.isArray(rolesRes) ? rolesRes : (Array.isArray(rolesRes?.data) ? rolesRes.data : []));
                setEmployeesForDropdown(Array.isArray(empRes) ? empRes : (Array.isArray(empRes?.data) ? empRes.data : []));
            } catch (e) {
                console.warn('Onay akışı listeleri yüklenemedi:', e);
            }
        };
        loadLookups();
        return () => {
            alive = false;
        };
    }, []);

    // Genel alan değişimi
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setLocalValidationError('');

        if (name === 'kod') {
            setFormData((prev) => ({
                ...prev,
                kod: value.toUpperCase().slice(0, 3),
            }));
            return;
        }

        if (type === 'checkbox') {
            setFormData((prev) => {
                const updated = { ...prev, [name]: checked };

                if (name === 'onayGereklidir') {
                    setApprovalError('');
                    if (!checked) {
                        updated.adminOnaysizGirebilirMi = false;
                        setApprovalSteps([]);
                    } else if (approvalSteps.length === 0) {
                        setApprovalSteps([
                            {
                                sira: 1,
                                onayciTipi: 1,
                                herhangiBiriOnaylayabilir: true,
                                roleIds: [],
                                userIds: [],
                            },
                        ]);
                    }
                }

                if (name === 'bakiyedenDusulsun' && !checked) {
                    updated.bakiyeModu = 0;
                    updated.isAnaYillikIzin = false;
                    updated.yillikDevredilsinMi = false;
                    updated.yillikDevredenSilmeYili = '';
                    updated.kidemKuralKullanilsinMi = false;
                    updated.yasKuralKullanilsinMi = false;
                    updated.eventPeriyodu = '';
                    updated.eventLimitBirimi = 1;
                    updated.eventLimitToplamGun = '';
                    updated.eventLimitAsimiYillikIzindenDusulsunMu = false;
                    updated.eventLimitAsimiHedefIzinTuruId = '';
                    updated.negatifBakiyeKullanilsinMi = false;
                    updated.negatifBakiyeGunLimiti = '';
                }

                if (name === 'eventLimitAsimiYillikIzindenDusulsunMu' && checked && !updated.eventLimitAsimiHedefIzinTuruId) {
                    updated.eventLimitAsimiHedefIzinTuruId = defaultTargetAnnualId;
                }

                return updated;
            });
            return;
        }

        if (name === 'kullanimBirimi' || name === 'odemeDurumu' || name === 'eventLimitBirimi') {
            const parsedVal = parseInt(value, 10);
            setFormData((prev) => ({
                ...prev,
                [name]: parsedVal,
                ...(name === 'kullanimBirimi' && Number(prev.bakiyeModu) === 2
                    ? { eventLimitBirimi: parsedVal === 2 ? 2 : prev.eventLimitBirimi }
                    : {}),
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Bakiye Modu Kart Seçimi
    const handleSelectBakiyeModu = (intVal) => {
        setLocalValidationError('');
        setFormData((prev) => {
            const updated = {
                ...prev,
                bakiyeModu: intVal,
                bakiyedenDusulsun: intVal > 0,
            };

            if (intVal !== 1) {
                updated.isAnaYillikIzin = false;
                updated.yillikDevredilsinMi = false;
                updated.yillikDevredenSilmeYili = '';
                updated.kidemKuralKullanilsinMi = false;
                updated.yasKuralKullanilsinMi = false;
                updated.negatifBakiyeKullanilsinMi = intVal === 2 ? prev.negatifBakiyeKullanilsinMi : false;
                updated.negatifBakiyeGunLimiti = intVal === 2 ? prev.negatifBakiyeGunLimiti : '';
            } else {
                updated.yillikDevredilsinMi = true;
                // Sistemde henüz aktif bir Ana Yıllık İzin yoksa otomatik işaretle; varsa kesinlikle false tut
                updated.isAnaYillikIzin = !existingActivePrimaryAnnual;
            }

            if (intVal !== 2) {
                updated.eventPeriyodu = '';
                updated.eventLimitBirimi = 1;
                updated.eventLimitToplamGun = '';
                updated.eventLimitAsimiYillikIzindenDusulsunMu = false;
                updated.eventLimitAsimiHedefIzinTuruId = '';
            } else {
                if (!updated.eventPeriyodu) {
                    updated.eventPeriyodu = '3'; // Varsayılan: Olay Başına / Tek Seferlik
                }
                updated.eventLimitBirimi = Number(prev.kullanimBirimi) === 2 ? 2 : (prev.eventLimitBirimi || 1);
            }

            return updated;
        });
    };

    // ====== KIDEM & YAŞ KURALLARI YÖNETİMİ ======
    const handleAddKidemRule = () => {
        setKidemRules((prev) => [
            ...prev,
            { id: null, minKidemYil: '', maxKidemYil: '', gunSayisi: '' },
        ]);
    };

    const handleKidemRuleChange = (index, field, value) => {
        setLocalValidationError('');
        setKidemRules((prev) =>
            prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
        );
    };

    const handleDeleteKidemRule = (index) => {
        setKidemRules((prev) => prev.filter((_, i) => i !== index));
    };

    // 4857 Sayılı İş Kanunu Standart Kıdem Şablonu
    const applyLegalSeniorityTemplate = () => {
        setFormData((prev) => ({ ...prev, kidemKuralKullanilsinMi: true }));
        setKidemRules([
            { id: null, minKidemYil: '1', maxKidemYil: '5', gunSayisi: '14' },
            { id: null, minKidemYil: '6', maxKidemYil: '14', gunSayisi: '20' },
            { id: null, minKidemYil: '15', maxKidemYil: '', gunSayisi: '26' },
        ]);
    };

    const handleAddYasRule = () => {
        setYasRules((prev) => [
            ...prev,
            { id: null, minYas: '', maxYas: '', gunSayisi: '' },
        ]);
    };

    const handleYasRuleChange = (index, field, value) => {
        setLocalValidationError('');
        setYasRules((prev) =>
            prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
        );
    };

    const handleDeleteYasRule = (index) => {
        setYasRules((prev) => prev.filter((_, i) => i !== index));
    };

    // 4857 Sayılı İş Kanunu Koruyucu Yaş Şablonu (18 yaş altı ve 50 yaş üstü en az 20 gün)
    const applyLegalAgeTemplate = () => {
        setFormData((prev) => ({ ...prev, yasKuralKullanilsinMi: true }));
        setYasRules([
            { id: null, minYas: '0', maxYas: '18', gunSayisi: '20' },
            { id: null, minYas: '50', maxYas: '', gunSayisi: '20' },
        ]);
    };

    // ====== ONAY AKIŞI YARDIMCILARI ======
    const getEmployeeDisplayNameSafe = (emp) => {
        if (!emp) return '';
        return (
            emp.fullName ||
            emp.adSoyad ||
            `${emp.firstName || emp.ad || ''} ${emp.lastName || emp.soyad || ''}`.trim() ||
            emp.email ||
            `Personel #${emp.id}`
        );
    };

    const getApproverUserIdFromEmployee = (emp) => {
        const cand = emp?.userId ?? emp?.kullaniciId ?? emp?.user?.id ?? null;
        const n = parseInt(cand, 10);
        return Number.isNaN(n) ? null : n;
    };

    const normalizeStepOrders = (steps) =>
        steps.map((s, idx) => ({ ...s, sira: idx + 1 }));

    const addApprovalStep = () => {
        setApprovalError('');
        setApprovalSteps((prev) =>
            normalizeStepOrders([
                ...prev,
                {
                    sira: prev.length + 1,
                    onayciTipi: 1,
                    herhangiBiriOnaylayabilir: true,
                    roleIds: [],
                    userIds: [],
                },
            ])
        );
    };

    const removeApprovalStep = (index) => {
        setApprovalError('');
        setApprovalSteps((prev) => normalizeStepOrders(prev.filter((_, i) => i !== index)));
    };

    const updateApprovalStep = (index, field, value) => {
        setApprovalError('');
        setApprovalSteps((prev) =>
            prev.map((s, i) => {
                if (i !== index) return s;
                if (field === 'onayciTipi') {
                    const nextType = parseInt(value, 10);
                    return {
                        ...s,
                        onayciTipi: nextType,
                        roleIds: nextType === 1 ? s.roleIds : [],
                        userIds: nextType === 2 ? s.userIds : [],
                    };
                }
                return { ...s, [field]: value };
            })
        );
    };

    const toggleStepSelection = (stepIndex, listField, itemId) => {
        setApprovalError('');
        const numericId = Number(itemId);
        setApprovalSteps((prev) =>
            prev.map((s, i) => {
                if (i !== stepIndex) return s;
                const currentList = Array.isArray(s[listField]) ? s[listField] : [];
                const exists = currentList.includes(numericId);
                const nextList = exists
                    ? currentList.filter((id) => id !== numericId)
                    : [...currentList, numericId];
                return { ...s, [listField]: nextList };
            })
        );
    };

    // Kıdem / Yaş aralık çakışma kontrolü
    const validateRanges = (rules, minKey, maxKey, labelPrefix) => {
        const parsed = [];
        for (let i = 0; i < rules.length; i++) {
            const r = rules[i];
            const min = parseInt(r[minKey], 10);
            const max =
                r[maxKey] !== '' && r[maxKey] !== null && r[maxKey] !== undefined
                    ? parseInt(r[maxKey], 10)
                    : null;
            const gun = parseFloat(r.gunSayisi);

            if (Number.isNaN(min) || min < 0) {
                return `${labelPrefix} tablosunda ${i + 1}. satır için geçerli bir başlangıç değeri giriniz.`;
            }
            if (max !== null && (Number.isNaN(max) || max < min)) {
                return `${labelPrefix} tablosunda ${i + 1}. satırda üst sınır (${max}), alt sınırdan (${min}) küçük olamaz.`;
            }
            if (Number.isNaN(gun) || gun <= 0) {
                return `${labelPrefix} tablosunda ${i + 1}. satır için hak ediş gün sayısı 0'dan büyük olmalıdır.`;
            }
            parsed.push({ row: i + 1, min, max: max ?? Infinity });
        }

        for (let i = 0; i < parsed.length; i++) {
            for (let j = i + 1; j < parsed.length; j++) {
                const a = parsed[i];
                const b = parsed[j];
                if (a.min <= b.max && b.min <= a.max) {
                    return `${labelPrefix} tablosunda ${a.row}. satır ile ${b.row}. satırın aralıkları birbiriyle çakışıyor. Lütfen kesintisiz ve çakışmayan aralıklar tanımlayın.`;
                }
            }
        }
        return null;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLocalValidationError('');
        setApprovalError('');

        if (!formData.kod || formData.kod.trim().length !== 3) {
            setLocalValidationError('İzin kodu tam olarak 3 karakter olmalıdır (Örn: YIL, MZR, EVL).');
            return;
        }

        if (!formData.ad || !formData.ad.trim()) {
            setLocalValidationError('Lütfen izin türü adını giriniz.');
            return;
        }

        const bakiyeModuInt = normalizeBakiyeModu(formData.bakiyeModu);
        const bakiyedenDusulsunBool = bakiyeModuInt > 0;

        // Yıllık Hak Ediş Kuralları Doğrulaması
        if (bakiyeModuInt === 1) {
            if (formData.kidemKuralKullanilsinMi) {
                if (kidemRules.length === 0) {
                    setLocalValidationError('Kıdeme göre hak ediş seçildi ancak hiç kıdem kuralı satırı eklenmedi.');
                    return;
                }
                const err = validateRanges(kidemRules, 'minKidemYil', 'maxKidemYil', 'Kıdem kuralları');
                if (err) {
                    setLocalValidationError(err);
                    return;
                }
            }
            if (formData.yasKuralKullanilsinMi) {
                if (yasRules.length === 0) {
                    setLocalValidationError('Yaşa göre hak ediş seçildi ancak hiç yaş kuralı satırı eklenmedi.');
                    return;
                }
                const err = validateRanges(yasRules, 'minYas', 'maxYas', 'Yaş kuralları');
                if (err) {
                    setLocalValidationError(err);
                    return;
                }
            }
        }

        // Dönemsel / Olay Bazlı Limit Doğrulaması
        if (bakiyeModuInt === 2) {
            if (!formData.eventPeriyodu) {
                setLocalValidationError('Dönemsel / Olay bazlı limit modunda yenilenme periyodu (Aylık / Yıllık / Olay Başına) seçilmelidir.');
                return;
            }
            const limitGun = parseFloat(formData.eventLimitToplamGun);
            const birimLabel = Number(formData.eventLimitBirimi) === 2 ? 'saat' : 'gün';
            if (Number.isNaN(limitGun) || limitGun <= 0) {
                setLocalValidationError(`Dönemsel / Olay bazlı limit modunda tanımlanacak toplam ${birimLabel} limiti 0'dan büyük olmalıdır.`);
                return;
            }
            if (formData.eventLimitAsimiYillikIzindenDusulsunMu) {
                if (annualLeaveOptions.length === 0) {
                    setLocalValidationError('Sistemde henüz Yıllık Hak Edişli bir izin türü tanımlanmamış! Aşımın yıllık izinden düşülebilmesi için önce Yıllık İzin türünü tanımlamalısınız.');
                    return;
                }
                if (!formData.eventLimitAsimiHedefIzinTuruId) {
                    setLocalValidationError('Lütfen kota aşıldığında aşan sürenin düşüleceği hedef Yıllık İzin türünü seçiniz.');
                    return;
                }
            }
        }

        // Onay Süreci Doğrulaması
        if (formData.onayGereklidir) {
            if (!Array.isArray(approvalSteps) || approvalSteps.length === 0) {
                setApprovalError('Onay süreci aktif edildiğinde en az 1 onay adımı tanımlanmalıdır.');
                return;
            }
            for (let i = 0; i < approvalSteps.length; i++) {
                const s = approvalSteps[i];
                const stepNo = i + 1;
                const stepType = normalizeOnayciTipi(s.onayciTipi);
                if (stepType === 1 && (!s.roleIds || s.roleIds.length === 0)) {
                    setApprovalError(`${stepNo}. onay adımı (Rol Bazlı) için en az 1 rol seçmelisiniz.`);
                    return;
                }
                if (stepType === 2 && (!s.userIds || s.userIds.length === 0)) {
                    setApprovalError(`${stepNo}. onay adımı (Kişi Bazlı) için en az 1 onaylayıcı personel seçmelisiniz.`);
                    return;
                }
            }
        }

        const normalizeDoubleOrNull = (val) => {
            if (val === '' || val === null || val === undefined) return null;
            const num = parseFloat(val);
            return Number.isNaN(num) ? null : num;
        };

        const normalizeIntOrNull = (val) => {
            if (val === '' || val === null || val === undefined) return null;
            const num = parseInt(val, 10);
            return Number.isNaN(num) ? null : num;
        };

        const normalizedKidemRules =
            bakiyeModuInt === 1 && formData.kidemKuralKullanilsinMi
                ? kidemRules.map((r) => ({
                      id: r.id ?? null,
                      minKidemYil: parseInt(r.minKidemYil, 10),
                      maxKidemYil: normalizeIntOrNull(r.maxKidemYil),
                      gunSayisi: parseFloat(r.gunSayisi),
                  }))
                : [];

        const normalizedYasRules =
            bakiyeModuInt === 1 && formData.yasKuralKullanilsinMi
                ? yasRules.map((r) => ({
                      id: r.id ?? null,
                      minYas: parseInt(r.minYas, 10),
                      maxYas: normalizeIntOrNull(r.maxYas),
                      gunSayisi: parseFloat(r.gunSayisi),
                  }))
                : [];

        const payload = {
            kod: formData.kod.trim().toUpperCase(),
            ad: formData.ad.trim(),
            isActive: formData.isActive,
            kullanimBirimi: normalizeKullanimBirimi(formData.kullanimBirimi),
            odemeDurumu: normalizeOdemeDurumu(formData.odemeDurumu),
            bakiyedenDusulsun: bakiyedenDusulsunBool,
            varsayilanGunSaat: normalizeDoubleOrNull(formData.varsayilanGunSaat),

            bakiyeModu: bakiyeModuInt,
            isAnaYillikIzin:
                bakiyeModuInt === 1 && !existingActivePrimaryAnnual
                    ? Boolean(formData.isAnaYillikIzin)
                    : false,
            yillikDevredilsinMi: bakiyeModuInt === 1 ? Boolean(formData.yillikDevredilsinMi) : false,
            yillikDevredenSilmeYili:
                bakiyeModuInt === 1 && formData.yillikDevredilsinMi
                    ? normalizeIntOrNull(formData.yillikDevredenSilmeYili)
                    : null,
            kidemKuralKullanilsinMi: bakiyeModuInt === 1 ? Boolean(formData.kidemKuralKullanilsinMi) : false,
            yasKuralKullanilsinMi: bakiyeModuInt === 1 ? Boolean(formData.yasKuralKullanilsinMi) : false,

            eventPeriyodu: bakiyeModuInt === 2 ? normalizeEventPeriyodu(formData.eventPeriyodu) : null,
            eventLimitBirimi: bakiyeModuInt === 2 ? (Number(formData.eventLimitBirimi) === 2 ? 2 : 1) : 1,
            eventLimitToplamGun: bakiyeModuInt === 2 ? normalizeDoubleOrNull(formData.eventLimitToplamGun) : null,
            eventLimitAsimiYillikIzindenDusulsunMu:
                bakiyeModuInt === 2 ? Boolean(formData.eventLimitAsimiYillikIzindenDusulsunMu) : false,
            eventLimitAsimiHedefIzinTuruId:
                bakiyeModuInt === 2 && formData.eventLimitAsimiYillikIzindenDusulsunMu
                    ? normalizeIntOrNull(formData.eventLimitAsimiHedefIzinTuruId)
                    : null,

            negatifBakiyeKullanilsinMi: bakiyedenDusulsunBool
                ? Boolean(formData.negatifBakiyeKullanilsinMi)
                : false,
            negatifBakiyeGunLimiti:
                bakiyedenDusulsunBool && formData.negatifBakiyeKullanilsinMi
                    ? normalizeDoubleOrNull(formData.negatifBakiyeGunLimiti)
                    : null,

            resmiTatillerBakiyedenDusulsunMu: Boolean(formData.resmiTatillerBakiyedenDusulsunMu),
            haftaTatilleriBakiyedenDusulsunMu: Boolean(formData.haftaTatilleriBakiyedenDusulsunMu),

            onayGereklidir: Boolean(formData.onayGereklidir),
            adminOnaysizGirebilirMi: formData.onayGereklidir ? Boolean(formData.adminOnaysizGirebilirMi) : false,
            onayAdimlari: formData.onayGereklidir
                ? approvalSteps.map((s, i) => {
                      const stepType = normalizeOnayciTipi(s.onayciTipi);
                      return {
                          sira: i + 1,
                          onayciTipi: stepType,
                          herhangiBiriOnaylayabilir: Boolean(s.herhangiBiriOnayabilir ?? s.herhangiBiriOnaylayabilir ?? true),
                          roleIds: stepType === 1 ? s.roleIds : [],
                          userIds: stepType === 2 ? s.userIds : [],
                      };
                  })
                : [],

            kidemKurallari: normalizedKidemRules,
            yasKurallari: normalizedYasRules,
        };

        onSave(payload);
    };

    const currentBakiyeModu = normalizeBakiyeModu(formData.bakiyeModu);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col max-h-[92vh] overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                {/* Modal Üst Başlık */}
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-800/50 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-sm">
                            <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-slate-900 dark:text-white">
                                {isEditing ? 'İzin Türü Yapılandırmasını Düzenle' : 'Yeni İzin Türü Tanımla'}
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                İzin kurallarını, hak ediş hesaplama yöntemini ve onay adımlarını eksiksiz yapılandırın.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 dark:hover:bg-slate-800 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Modal Gövde (Scrollable) */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                    {(apiError || localValidationError) && (
                        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-sm flex items-start gap-2.5">
                            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                            <span>{localValidationError || apiError}</span>
                        </div>
                    )}

                    {/* BÖLÜM 1: TEMEL TANIM VE KULLANIM ŞEKLİ */}
                    <div className="p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20 space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-lg bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 text-xs font-bold flex items-center justify-center">
                                    1
                                </span>
                                <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                                    Temel İzin Bilgileri ve Kullanım Birimi
                                </h4>
                            </div>

                            {isEditing && (
                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleChange}
                                        className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                                        İzin Türü Aktif
                                    </span>
                                </label>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                            <div className="md:col-span-3">
                                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                    İzin Kodu (3 Karakter) *
                                </label>
                                <input
                                    type="text"
                                    name="kod"
                                    value={formData.kod}
                                    onChange={handleChange}
                                    maxLength={3}
                                    placeholder="Örn: YIL"
                                    required
                                    className="w-full px-3.5 py-2.5 text-sm font-mono font-bold uppercase rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                                />
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                                    Rapor ve puantaj kısaltması (YIL, MZR, RPR).
                                </p>
                            </div>

                            <div className="md:col-span-5">
                                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                    İzin Türü Adı *
                                </label>
                                <input
                                    type="text"
                                    name="ad"
                                    value={formData.ad}
                                    onChange={handleChange}
                                    placeholder="Örn: Yıllık Ücretli İzin, Evlilik İzni..."
                                    required
                                    className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                                />
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                                    Personel ve yöneticilerin talep ekranında göreceği ad.
                                </p>
                            </div>

                            <div className="md:col-span-4">
                                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                    1 Gün Kaç Saat Kabul Edilsin?
                                </label>
                                <input
                                    type="number"
                                    step="0.5"
                                    min="1"
                                    max="24"
                                    name="varsayilanGunSaat"
                                    value={formData.varsayilanGunSaat}
                                    onChange={handleChange}
                                    placeholder="Örn: 8 veya 7.5"
                                    className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                                />
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                                    Saatlik izinleri güne çevirirken kullanılır (Boşsa vardiyadan alınır).
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                    Kullanım Birimi
                                </label>
                                <select
                                    name="kullanimBirimi"
                                    value={formData.kullanimBirimi}
                                    onChange={handleChange}
                                    className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                                >
                                    <option value={1}>Günlük (Tam Gün Bazlı)</option>
                                    <option value={2}>Saatlik (Saat / Dakika Bazlı)</option>
                                    <option value={3}>Esnek (Talep Anında Günlük veya Saatlik Seçilebilir)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                    Puantaj ve Ödeme Durumu
                                </label>
                                <select
                                    name="odemeDurumu"
                                    value={formData.odemeDurumu}
                                    onChange={handleChange}
                                    className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                                >
                                    <option value={1}>Ücretli İzin (Maaş / Puantaj Kesintisi Yapılmaz)</option>
                                    <option value={2}>Ücretsiz İzin (Çalışılmayan Süre Kesilir)</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* BÖLÜM 2: TATİL GÜNLERİ VE SÜRE HESABI KURALLARI */}
                    <div className="p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20 space-y-3">
                        <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-lg bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 text-xs font-bold flex items-center justify-center">
                                2
                            </span>
                            <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                                Tatil Günleri ve Süre Hesaplama Kuralları
                            </h4>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="flex items-start gap-3 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 cursor-pointer hover:border-blue-400 transition-colors">
                                <input
                                    type="checkbox"
                                    name="haftaTatilleriBakiyedenDusulsunMu"
                                    checked={formData.haftaTatilleriBakiyedenDusulsunMu}
                                    onChange={handleChange}
                                    className="mt-0.5 h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                                />
                                <div>
                                    <span className="block text-xs font-semibold text-slate-800 dark:text-slate-100">
                                        Hafta Tatilleri İzin Süresine Dahil Edilsin (Düşülsün)
                                    </span>
                                    <span className="block text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                        İşaretlenmezse izin tarih aralığına denk gelen hafta tatili günleri izin bakiyesinden düşülmez.
                                    </span>
                                </div>
                            </label>

                            <label className="flex items-start gap-3 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 cursor-pointer hover:border-blue-400 transition-colors">
                                <input
                                    type="checkbox"
                                    name="resmiTatillerBakiyedenDusulsunMu"
                                    checked={formData.resmiTatillerBakiyedenDusulsunMu}
                                    onChange={handleChange}
                                    className="mt-0.5 h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                                />
                                <div>
                                    <span className="block text-xs font-semibold text-slate-800 dark:text-slate-100">
                                        Resmi Tatiller İzin Süresine Dahil Edilsin (Düşülsün)
                                    </span>
                                    <span className="block text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                        İşaretlenmezse resmi/genel tatil günleri net izin süresinden hariç tutulur.
                                    </span>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* BÖLÜM 3: BAKİYE, HAK EDİŞ VE DÖNEMSEL LİMİT MODELİ */}
                    <div className="p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20 space-y-5">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-lg bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 text-xs font-bold flex items-center justify-center">
                                    3
                                </span>
                                <div>
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                                        Bakiye, Hak Ediş ve Kullanım Limiti Modeli
                                    </h4>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Bu izin türünün bakiye takibi yapılıp yapılmayacağını ve kota/hak ediş türünü seçin.
                                    </p>
                                </div>
                            </div>

                            <span
                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-semibold border ${
                                    currentBakiyeModu > 0
                                        ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800/60'
                                        : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
                                }`}
                            >
                                {currentBakiyeModu > 0 ? 'Bakiye / Limit Takibi Aktif' : 'Bakiyesiz (Kayıt Amaçlı)'}
                            </span>
                        </div>

                        {/* 3'lü Bakiye Modu Seçim Kartları */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {/* Mod 0: Bakiyesiz */}
                            <button
                                type="button"
                                onClick={() => handleSelectBakiyeModu(0)}
                                className={`text-left p-4 rounded-xl border transition-all ${
                                    currentBakiyeModu === 0
                                        ? 'border-blue-600 bg-blue-50/60 dark:bg-blue-950/40 ring-2 ring-blue-500/20'
                                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs font-bold text-slate-900 dark:text-white">
                                        Bakiyesiz / Limitsiz
                                    </span>
                                    {currentBakiyeModu === 0 && <CheckCircle2 className="w-4 h-4 text-blue-600" />}
                                </div>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                                    Herhangi bir yıllık hak ediş veya dönemsel kota sınırı uygulanmaz (Örn: Ücretsiz İzin, İstirahat Raporu).
                                </p>
                            </button>

                            {/* Mod 1: Yıllık Hak Ediş */}
                            <button
                                type="button"
                                onClick={() => handleSelectBakiyeModu(1)}
                                className={`text-left p-4 rounded-xl border transition-all ${
                                    currentBakiyeModu === 1
                                        ? 'border-blue-600 bg-blue-50/60 dark:bg-blue-950/40 ring-2 ring-blue-500/20'
                                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs font-bold text-blue-700 dark:text-blue-300">
                                        Yıllık Hak Ediş (Kıdem / Yaş)
                                    </span>
                                    {currentBakiyeModu === 1 && <CheckCircle2 className="w-4 h-4 text-blue-600" />}
                                </div>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                                    İşe giriş yıl dönümünde personelin kıdem yılı ve yaşına göre otomatik hak kazanılan izinler (Örn: Yıllık İzin).
                                </p>
                            </button>

                            {/* Mod 2: Dönemsel / Olay Bazlı Limit */}
                            <button
                                type="button"
                                onClick={() => handleSelectBakiyeModu(2)}
                                className={`text-left p-4 rounded-xl border transition-all ${
                                    currentBakiyeModu === 2
                                        ? 'border-violet-600 bg-violet-50/60 dark:bg-violet-950/40 ring-2 ring-violet-500/20'
                                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs font-bold text-violet-700 dark:text-violet-300">
                                        Dönemsel / Olay Bazlı Limit
                                    </span>
                                    {currentBakiyeModu === 2 && <CheckCircle2 className="w-4 h-4 text-violet-600" />}
                                </div>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                                    Aylık, yıllık veya olay başına (evlilik, doğum, vefat, mazeret vb.) belirli bir gün kotası olan izinler.
                                </p>
                            </button>
                        </div>

                        {/* MOD 1 DETAYLARI: YILLIK HAK EDİŞ, DEVİR, KIDEM VE YAŞ TABLOLARI */}
                        {currentBakiyeModu === 1 && (
                            <div className="space-y-5 pt-2">
                                {/* ANA YILLIK İZİN İŞARETLEME KARTI (Sadece sistemde başka aktif Ana Yıllık İzin yoksa veya mevcut Ana Yıllık İzin düzenleniyorsa gösterilir) */}
                                {!existingActivePrimaryAnnual && (
                                    <div className="p-4 rounded-xl bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <label className="inline-flex items-start gap-2.5 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="isAnaYillikIzin"
                                                checked={formData.isAnaYillikIzin}
                                                onChange={handleChange}
                                                className="mt-0.5 h-4 w-4 rounded text-amber-600 focus:ring-amber-500"
                                            />
                                            <div>
                                                <span className="flex items-center gap-2 text-xs font-bold text-amber-950 dark:text-amber-200">
                                                    Bu İzin Türü Firmanın Ana (Resmi) Yıllık İzin Türüdür
                                                    <span className="px-2 py-0.5 rounded bg-amber-200/80 dark:bg-amber-900/60 text-[10px] font-bold text-amber-900 dark:text-amber-200">
                                                        ★ Sistem Yıllık İzni
                                                    </span>
                                                </span>
                                                <span className="block text-[11px] text-amber-800/90 dark:text-amber-300/80 mt-0.5">
                                                    İzin adını nasıl yazarsanız yazın veya birden fazla yıllık hak edişli izin türünüz (örn: İdari Kıdem İzni vb.) olsa bile, sistem bu işarete sahip izin türünü resmi Yıllık İzin olarak tanır.
                                                </span>
                                            </div>
                                        </label>
                                    </div>
                                )}

                                {/* Devir Ayarları */}
                                <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 space-y-3">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <label className="inline-flex items-center gap-2.5 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="yillikDevredilsinMi"
                                                checked={formData.yillikDevredilsinMi}
                                                onChange={handleChange}
                                                className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                                            />
                                            <div>
                                                <span className="block text-xs font-bold text-slate-800 dark:text-slate-100">
                                                    Kullanılmayan İzin Hakları Sonraki Yıla Devredilsin
                                                </span>
                                                <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                                                    Dönem sonunda kalan pozitif bakiye yeni çalışma yılına aktarılır.
                                                </span>
                                            </div>
                                        </label>

                                        {formData.yillikDevredilsinMi && (
                                            <div className="flex items-center gap-2">
                                                <label className="text-xs font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                                    Devir Geçerlilik Süresi (Yıl):
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    name="yillikDevredenSilmeYili"
                                                    value={formData.yillikDevredenSilmeYili}
                                                    onChange={handleChange}
                                                    placeholder="Sınırsız"
                                                    className="w-28 px-3 py-1.5 text-xs rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* KIDEM KURALLARI TABLOSU */}
                                <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 space-y-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                        <label className="inline-flex items-center gap-2.5 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="kidemKuralKullanilsinMi"
                                                checked={formData.kidemKuralKullanilsinMi}
                                                onChange={handleChange}
                                                className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                                            />
                                            <div>
                                                <span className="block text-xs font-bold text-slate-900 dark:text-white">
                                                    Kıdeme Göre Kademeli Hak Ediş Tablosu Kullan
                                                </span>
                                                <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                                                    Personelin çalışma yılına (kıdemine) göre kazanacağı yıllık izin gün sayısını belirler.
                                                </span>
                                            </div>
                                        </label>

                                        <button
                                            type="button"
                                            onClick={applyLegalSeniorityTemplate}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:hover:bg-blue-900/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60 transition-colors"
                                        >
                                            <Sparkles className="w-3.5 h-3.5" />
                                            İş Kanunu Standartlarını Doldur (14 / 20 / 26 Gün)
                                        </button>
                                    </div>

                                    {formData.kidemKuralKullanilsinMi && (
                                        <div className="space-y-3">
                                            {kidemRules.length === 0 ? (
                                                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center text-xs text-slate-500 dark:text-slate-400">
                                                    Henüz kıdem aralığı eklenmedi. &ldquo;Yeni Kıdem Aralığı Ekle&rdquo; veya &ldquo;İş Kanunu Standartlarını Doldur&rdquo; butonunu kullanabilirsiniz.
                                                </div>
                                            ) : (
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full text-xs">
                                                        <thead>
                                                            <tr className="text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                                                                <th className="py-2 pr-3 text-left font-semibold">Alt Kıdem (Yıl) *</th>
                                                                <th className="py-2 px-3 text-left font-semibold">Üst Kıdem (Yıl - Boş = Sınırsız)</th>
                                                                <th className="py-2 px-3 text-left font-semibold">Yıllık Hak Ediş (Gün) *</th>
                                                                <th className="py-2 pl-3 text-right font-semibold">Sil</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                            {kidemRules.map((rule, index) => (
                                                                <tr key={index}>
                                                                    <td className="py-2 pr-3">
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            value={rule.minKidemYil}
                                                                            onChange={(e) =>
                                                                                handleKidemRuleChange(index, 'minKidemYil', e.target.value)
                                                                            }
                                                                            placeholder="Örn: 1"
                                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white"
                                                                        />
                                                                    </td>
                                                                    <td className="py-2 px-3">
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            value={rule.maxKidemYil}
                                                                            onChange={(e) =>
                                                                                handleKidemRuleChange(index, 'maxKidemYil', e.target.value)
                                                                            }
                                                                            placeholder="Boş = Sınırsız (Örn: 5)"
                                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white"
                                                                        />
                                                                    </td>
                                                                    <td className="py-2 px-3">
                                                                        <input
                                                                            type="number"
                                                                            step="0.5"
                                                                            min="0.5"
                                                                            value={rule.gunSayisi}
                                                                            onChange={(e) =>
                                                                                handleKidemRuleChange(index, 'gunSayisi', e.target.value)
                                                                            }
                                                                            placeholder="Örn: 14"
                                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-semibold"
                                                                        />
                                                                    </td>
                                                                    <td className="py-2 pl-3 text-right">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDeleteKidemRule(index)}
                                                                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40"
                                                                            title="Satırı Kaldır"
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}

                                            <button
                                                type="button"
                                                onClick={handleAddKidemRule}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors"
                                            >
                                                <PlusCircle className="w-3.5 h-3.5" />
                                                Yeni Kıdem Aralığı Ekle
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* YAŞ KURALLARI TABLOSU */}
                                <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 space-y-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                        <label className="inline-flex items-center gap-2.5 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="yasKuralKullanilsinMi"
                                                checked={formData.yasKuralKullanilsinMi}
                                                onChange={handleChange}
                                                className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                                            />
                                            <div>
                                                <span className="block text-xs font-bold text-slate-900 dark:text-white">
                                                    Yaşa Göre Koruyucu Alt Sınır Kuralları Kullan
                                                </span>
                                                <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                                                    Hem kıdem hem yaş kuralı aktifse, personel lehine olan (daha yüksek gün veren) kural otomatik uygulanır.
                                                </span>
                                            </div>
                                        </label>

                                        <button
                                            type="button"
                                            onClick={applyLegalAgeTemplate}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:hover:bg-emerald-900/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60 transition-colors"
                                        >
                                            <Sparkles className="w-3.5 h-3.5" />
                                            Yasal Yaş Kuralını Doldur (≤18 ve ≥50 Yaş: 20 Gün)
                                        </button>
                                    </div>

                                    {formData.yasKuralKullanilsinMi && (
                                        <div className="space-y-3">
                                            {yasRules.length === 0 ? (
                                                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center text-xs text-slate-500 dark:text-slate-400">
                                                    Henüz yaş aralığı eklenmedi. &ldquo;Yeni Yaş Aralığı Ekle&rdquo; veya &ldquo;Yasal Yaş Kuralını Doldur&rdquo; butonunu kullanabilirsiniz.
                                                </div>
                                            ) : (
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full text-xs">
                                                        <thead>
                                                            <tr className="text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                                                                <th className="py-2 pr-3 text-left font-semibold">Alt Yaş Sınırı *</th>
                                                                <th className="py-2 px-3 text-left font-semibold">Üst Yaş Sınırı (Boş = Sınırsız)</th>
                                                                <th className="py-2 px-3 text-left font-semibold">Minimum Hak Ediş (Gün) *</th>
                                                                <th className="py-2 pl-3 text-right font-semibold">Sil</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                            {yasRules.map((rule, index) => (
                                                                <tr key={index}>
                                                                    <td className="py-2 pr-3">
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            value={rule.minYas}
                                                                            onChange={(e) =>
                                                                                handleYasRuleChange(index, 'minYas', e.target.value)
                                                                            }
                                                                            placeholder="Örn: 50"
                                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white"
                                                                        />
                                                                    </td>
                                                                    <td className="py-2 px-3">
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            value={rule.maxYas}
                                                                            onChange={(e) =>
                                                                                handleYasRuleChange(index, 'maxYas', e.target.value)
                                                                            }
                                                                            placeholder="Boş = Sınırsız (50+ yaş için boş bırakın)"
                                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white"
                                                                        />
                                                                    </td>
                                                                    <td className="py-2 px-3">
                                                                        <input
                                                                            type="number"
                                                                            step="0.5"
                                                                            min="0.5"
                                                                            value={rule.gunSayisi}
                                                                            onChange={(e) =>
                                                                                handleYasRuleChange(index, 'gunSayisi', e.target.value)
                                                                            }
                                                                            placeholder="Örn: 20"
                                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-semibold"
                                                                        />
                                                                    </td>
                                                                    <td className="py-2 pl-3 text-right">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDeleteYasRule(index)}
                                                                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40"
                                                                            title="Satırı Kaldır"
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}

                                            <button
                                                type="button"
                                                onClick={handleAddYasRule}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors"
                                            >
                                                <PlusCircle className="w-3.5 h-3.5" />
                                                Yeni Yaş Aralığı Ekle
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* MOD 2 DETAYLARI: DÖNEMSEL / OLAY BAZLI LİMİT AYARLARI */}
                        {currentBakiyeModu === 2 && (
                            <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-violet-200 dark:border-violet-900/60 space-y-4">
                                <div className="flex items-start gap-2.5">
                                    <Info className="w-4 h-4 text-violet-600 dark:text-violet-400 shrink-0 mt-0.5" />
                                    <p className="text-xs text-slate-600 dark:text-slate-300">
                                        <strong>Dönemsel / Olay Bazlı Limit:</strong> Evlilik izni (3 gün), babalık izni (5 gün), vefat izni veya aylık/yıllık saatlik mazeret izni gibi belirli bir periyotta üst sınırı olan izinler için kullanılır. Limit birimini doğrudan <strong>Gün</strong> veya <strong>Saat</strong> olarak belirleyebilirsiniz.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                            Limit Yenilenme Periyodu *
                                        </label>
                                        <select
                                            name="eventPeriyodu"
                                            value={formData.eventPeriyodu}
                                            onChange={handleChange}
                                            className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white"
                                        >
                                            <option value="">Periyot Seçiniz...</option>
                                            <option value="1">Aylık Kota (Her ay başında yenilenir)</option>
                                            <option value="2">Yıllık Kota (Her takvim yılında yenilenir)</option>
                                            <option value="3">Olay Başına / Tek Seferlik (Evlilik, Doğum vb.)</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                            Limit Birimi (Müşteri Ayarı) *
                                        </label>
                                        <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setFormData((prev) => ({ ...prev, eventLimitBirimi: 1 }))
                                                }
                                                className={`py-2 px-3 rounded-lg text-xs font-bold transition-all ${
                                                    Number(formData.eventLimitBirimi) !== 2
                                                        ? 'bg-white dark:bg-slate-900 text-violet-700 dark:text-violet-300 shadow-sm border border-violet-200 dark:border-violet-800/60'
                                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                                                }`}
                                            >
                                                Gün Bazlı Limit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setFormData((prev) => ({ ...prev, eventLimitBirimi: 2 }))
                                                }
                                                className={`py-2 px-3 rounded-lg text-xs font-bold transition-all ${
                                                    Number(formData.eventLimitBirimi) === 2
                                                        ? 'bg-white dark:bg-slate-900 text-violet-700 dark:text-violet-300 shadow-sm border border-violet-200 dark:border-violet-800/60'
                                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                                                }`}
                                            >
                                                Saat Bazlı Limit
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                            Periyot Başına Toplam Hak ({Number(formData.eventLimitBirimi) === 2 ? 'Saat' : 'Gün'}) *
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                step="0.5"
                                                min="0.5"
                                                name="eventLimitToplamGun"
                                                value={formData.eventLimitToplamGun}
                                                onChange={handleChange}
                                                placeholder={Number(formData.eventLimitBirimi) === 2 ? 'Örn: 6 veya 16 Saat' : 'Örn: 3 veya 5 Gün'}
                                                className="w-full pl-3.5 pr-16 py-2.5 text-sm rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-semibold"
                                            />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 rounded-md text-xs font-bold bg-violet-100 dark:bg-violet-950/70 text-violet-700 dark:text-violet-300">
                                                {Number(formData.eventLimitBirimi) === 2 ? 'SAAT' : 'GÜN'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-3">
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="eventLimitAsimiYillikIzindenDusulsunMu"
                                            checked={formData.eventLimitAsimiYillikIzindenDusulsunMu}
                                            onChange={handleChange}
                                            className="mt-0.5 h-4 w-4 rounded text-violet-600 focus:ring-violet-500"
                                        />
                                        <div>
                                            <span className="block text-xs font-semibold text-slate-800 dark:text-slate-100">
                                                Kota Aşılırsa Aşan Süre Yıllık İzin / Hak Ediş Bakiyesinden Düşülsün
                                            </span>
                                            <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                                                Örn: Belirlenen mazeret kotası dolduğunda personel ek izin talep ederse aşan kısım aşağıda seçilen izin türünün bakiyesinden mahsup edilir.
                                            </span>
                                        </div>
                                    </label>

                                    {formData.eventLimitAsimiYillikIzindenDusulsunMu && (
                                        <div className="p-3.5 rounded-xl bg-violet-50/70 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800/60 space-y-2">
                                            <label className="block text-xs font-bold text-violet-950 dark:text-violet-200">
                                                Aşımın Düşüleceği Hedef İzin Türü (Yıllık İzin Seçimi) *
                                            </label>
                                            {annualLeaveOptions.length === 0 ? (
                                                <div className="flex items-center gap-2 text-xs text-amber-800 dark:text-amber-300 bg-amber-100/80 dark:bg-amber-950/50 p-2.5 rounded-lg border border-amber-300 dark:border-amber-800">
                                                    <AlertTriangle className="w-4 h-4 shrink-0" />
                                                    <span>
                                                        Sistemde henüz <strong>Yıllık Hak Ediş</strong> modunda bir izin türü tanımlanmamış! Bu özelliği kullanabilmek için önce Yıllık İzin türünü eklemelisiniz.
                                                    </span>
                                                </div>
                                            ) : (
                                                <>
                                                    <select
                                                        name="eventLimitAsimiHedefIzinTuruId"
                                                        value={formData.eventLimitAsimiHedefIzinTuruId}
                                                        onChange={handleChange}
                                                        className="w-full px-3.5 py-2 text-xs font-semibold rounded-xl border border-violet-300 dark:border-violet-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                                    >
                                                        <option value="">Hedef İzin Türü Seçiniz...</option>
                                                        {annualLeaveOptions.map((opt) => (
                                                            <option key={opt.id} value={String(opt.id)}>
                                                                {opt.ad} ({opt.kod}){opt.isAnaYillikIzin ? ' — ★ Ana Yıllık İzin' : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <p className="text-[11px] text-violet-700 dark:text-violet-300">
                                                        İsim hatası veya birden fazla yıllık hak edişli izin türü olması durumunda karışıklığı önlemek için aşım doğrudan seçtiğiniz bu izin türünün bakiyesinden düşülür.
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* AVANS / NEGATİF BAKİYE AYARLARI (Yıllık veya Dönemsel modda) */}
                        {currentBakiyeModu > 0 && (
                            <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <label className="inline-flex items-start gap-2.5 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="negatifBakiyeKullanilsinMi"
                                        checked={formData.negatifBakiyeKullanilsinMi}
                                        onChange={handleChange}
                                        className="mt-0.5 h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                                    />
                                    <div>
                                        <span className="block text-xs font-bold text-slate-800 dark:text-slate-100">
                                            Avans İzin (Eksi Bakiyeye Düşme) Kullanımına İzin Ver
                                        </span>
                                        <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                                            Personelin mevcut hakkı yetmediğinde gelecekteki hak edişinden düşülmek üzere eksi bakiyeye inebilir.
                                        </span>
                                    </div>
                                </label>

                                {formData.negatifBakiyeKullanilsinMi && (
                                    <div className="flex items-center gap-2 shrink-0">
                                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                                            Maksimum Eksi Limit (Gün):
                                        </label>
                                        <input
                                            type="number"
                                            step="0.5"
                                            min="0.5"
                                            name="negatifBakiyeGunLimiti"
                                            value={formData.negatifBakiyeGunLimiti}
                                            onChange={handleChange}
                                            placeholder="Sınırsız"
                                            className="w-28 px-3 py-1.5 text-xs rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* BÖLÜM 4: ONAY SÜRECİ VE İŞ AKIŞI */}
                    <div className="p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20 space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-lg bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 text-xs font-bold flex items-center justify-center">
                                    4
                                </span>
                                <div>
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                                        Onay Süreci ve Kademeli İş Akışı
                                    </h4>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        İzin taleplerinin hangi yönetici rolleri veya kişiler tarafından sırasıyla onaylanacağını belirleyin.
                                    </p>
                                </div>
                            </div>

                            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="onayGereklidir"
                                    checked={Boolean(formData.onayGereklidir)}
                                    onChange={handleChange}
                                    className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                                    Onay Akışı Kullanılsın
                                </span>
                            </label>
                        </div>

                        {formData.onayGereklidir && (
                            <div className="space-y-4 pt-1">
                                <label className="flex items-center gap-2.5 p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="adminOnaysizGirebilirMi"
                                        checked={Boolean(formData.adminOnaysizGirebilirMi)}
                                        onChange={handleChange}
                                        className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                                        Yetkili İK / Yönetici kullanıcıları başka personeller adına <strong>onay sürecini bekletmeden (direkt onaylı)</strong> izin kaydı girebilsin (Kendi izin talepleri hariç).
                                    </span>
                                </label>

                                {approvalError && (
                                    <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4 shrink-0" />
                                        <span>{approvalError}</span>
                                    </div>
                                )}

                                {/* Adım Kartları */}
                                <div className="space-y-3">
                                    {approvalSteps.map((step, idx) => {
                                        const isRoleStep = Number(step.onayciTipi) === 1;
                                        const searchVal = (stepSearchTerms[idx] || '').trim().toLowerCase();

                                        const availableRoles = rolesForDropdown.filter((r) => {
                                            const label = (r.name || r.ad || r.label || r.roleName || '').toLowerCase();
                                            return !searchVal || label.includes(searchVal);
                                        });

                                        const availableEmployees = employeesForDropdown.filter((emp) => {
                                            const uid = getApproverUserIdFromEmployee(emp);
                                            if (!uid) return false;
                                            const label = getEmployeeDisplayNameSafe(emp).toLowerCase();
                                            const dept = (emp.departmentName || '').toLowerCase();
                                            return !searchVal || label.includes(searchVal) || dept.includes(searchVal);
                                        });

                                        return (
                                            <div
                                                key={idx}
                                                className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 space-y-3 shadow-sm"
                                            >
                                                <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-slate-100 dark:border-slate-800">
                                                    <div className="flex items-center gap-2">
                                                        <span className="px-2.5 py-1 rounded-lg bg-blue-600 text-white text-xs font-bold">
                                                            {idx + 1}. Onay Adımı
                                                        </span>
                                                        {idx > 0 && (
                                                            <span className="text-[11px] text-slate-400 flex items-center gap-1">
                                                                <ArrowRight className="w-3 h-3" /> {idx}. adım onaylandıktan sonra devreye girer
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        {/* Onaycı Tipi Seçici */}
                                                        <div className="inline-flex rounded-lg p-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                                            <button
                                                                type="button"
                                                                onClick={() => updateApprovalStep(idx, 'onayciTipi', 1)}
                                                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                                                                    isRoleStep
                                                                        ? 'bg-white dark:bg-slate-900 text-blue-600 dark:text-blue-400 shadow-sm'
                                                                        : 'text-slate-600 dark:text-slate-400'
                                                                }`}
                                                            >
                                                                <Users className="w-3.5 h-3.5" />
                                                                Rol Bazlı
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => updateApprovalStep(idx, 'onayciTipi', 2)}
                                                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                                                                    !isRoleStep
                                                                        ? 'bg-white dark:bg-slate-900 text-blue-600 dark:text-blue-400 shadow-sm'
                                                                        : 'text-slate-600 dark:text-slate-400'
                                                                }`}
                                                            >
                                                                <UserCheck className="w-3.5 h-3.5" />
                                                                Kişi Bazlı
                                                            </button>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => removeApprovalStep(idx)}
                                                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40"
                                                            title="Bu Adımı Sil"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Arama ve Tıklanabilir Seçim Alanı */}
                                                <div className="space-y-2">
                                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                                                            {isRoleStep
                                                                ? `Onaylayabilecek Roller (${step.roleIds?.length || 0} seçili)`
                                                                : `Onaylayabilecek Kişiler (${step.userIds?.length || 0} seçili)`}
                                                        </label>
                                                        <div className="relative w-full sm:w-64">
                                                            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                                            <input
                                                                type="text"
                                                                value={stepSearchTerms[idx] || ''}
                                                                onChange={(e) =>
                                                                    setStepSearchTerms((prev) => ({
                                                                        ...prev,
                                                                        [idx]: e.target.value,
                                                                    }))
                                                                }
                                                                placeholder={isRoleStep ? 'Rol ara...' : 'Personel veya departman ara...'}
                                                                className="w-full pl-8 pr-3 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                                                            />
                                                        </div>
                                                    </div>

                                                    {isRoleStep ? (
                                                        <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/70 dark:border-slate-700/60">
                                                            {availableRoles.length === 0 ? (
                                                                <span className="text-xs text-slate-400 py-2">
                                                                    Eşleşen rol bulunamadı.
                                                                </span>
                                                            ) : (
                                                                availableRoles.map((r) => {
                                                                    const selected = (step.roleIds || []).includes(Number(r.id));
                                                                    const label = r.name || r.ad || r.label || r.roleName;
                                                                    return (
                                                                        <button
                                                                            key={r.id}
                                                                            type="button"
                                                                            onClick={() => toggleStepSelection(idx, 'roleIds', r.id)}
                                                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                                                                selected
                                                                                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                                                                    : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-blue-400'
                                                                            }`}
                                                                        >
                                                                            {selected && <Check className="w-3.5 h-3.5" />}
                                                                            <span>{label}</span>
                                                                        </button>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/70 dark:border-slate-700/60">
                                                            {availableEmployees.length === 0 ? (
                                                                <span className="text-xs text-slate-400 py-2">
                                                                    Seçilebilir kullanıcı hesabı olan personel bulunamadı.
                                                                </span>
                                                            ) : (
                                                                availableEmployees.map((emp) => {
                                                                    const uid = getApproverUserIdFromEmployee(emp);
                                                                    const selected = (step.userIds || []).includes(Number(uid));
                                                                    const name = getEmployeeDisplayNameSafe(emp);
                                                                    return (
                                                                        <button
                                                                            key={uid}
                                                                            type="button"
                                                                            onClick={() => toggleStepSelection(idx, 'userIds', uid)}
                                                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                                                                selected
                                                                                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                                                                    : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-blue-400'
                                                                            }`}
                                                                        >
                                                                            {selected && <Check className="w-3.5 h-3.5" />}
                                                                            <span>{name}</span>
                                                                            {emp.departmentName && (
                                                                                <span
                                                                                    className={`text-[10px] ${
                                                                                        selected ? 'text-blue-100' : 'text-slate-400'
                                                                                    }`}
                                                                                >
                                                                                    ({emp.departmentName})
                                                                                </span>
                                                                            )}
                                                                        </button>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="flex items-center justify-between pt-1">
                                                    <label className="inline-flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={Boolean(step.herhangiBiriOnaylayabilir)}
                                                            onChange={(e) =>
                                                                updateApprovalStep(
                                                                    idx,
                                                                    'herhangiBiriOnaylayabilir',
                                                                    e.target.checked
                                                                )
                                                            }
                                                            className="h-3.5 w-3.5 rounded text-blue-600"
                                                        />
                                                        <span className="text-xs text-slate-600 dark:text-slate-300">
                                                            Bu adımda seçilen onaylayıcılardan <strong>herhangi birinin</strong> onaylaması sonraki adıma geçmek için yeterlidir.
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    <button
                                        type="button"
                                        onClick={addApprovalStep}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:hover:bg-blue-900/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60 transition-colors"
                                    >
                                        <PlusCircle className="w-4 h-4" />
                                        Yeni Onay Adımı Ekle ({approvalSteps.length + 1}. Adım)
                                    </button>

                                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                        Not: Onaylayıcılar yalnızca kendi veri görme kapsamı (departman/yetki alanı) dahilindeki personellerin izin taleplerini görüntüleyebilir ve onaylayabilir.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Modal Alt Aksiyon Çubuğu */}
                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-200 dark:border-slate-800">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isSubmitting}
                            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors"
                        >
                            Vazgeç
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 shadow-sm transition-colors"
                        >
                            {isSubmitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            {isEditing ? 'Değişiklikleri Kaydet' : 'İzin Türünü Oluştur'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ============== SİLME ONAY MODALI ==============
const ConfirmationModal = ({ show, title, onConfirm, onCancel, children }) => {
    if (!show) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl max-w-md w-full p-6 space-y-5">
                <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 flex items-center justify-center text-red-600 dark:text-red-400 shrink-0">
                        <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div className="space-y-1">
                        <h3 className="text-base font-bold text-slate-900 dark:text-white">{title}</h3>
                        <div>{children}</div>
                    </div>
                </div>
                <div className="flex justify-end gap-2.5 pt-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                    >
                        Vazgeç
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        className="px-4 py-2 rounded-xl text-xs font-semibold bg-red-600 hover:bg-red-700 text-white shadow-sm"
                    >
                        Evet, Sil
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============== KULLANIM UYARI MODALI ==============
const UsageWarningModal = ({ show, leaveType, usageDetails, onConfirm, onCancel }) => {
    if (!show) return null;

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl max-w-2xl w-full p-6 flex flex-col max-h-[85vh]">
                <div className="flex items-start gap-3.5 pb-4 border-b border-slate-200 dark:border-slate-800">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 flex items-center justify-center text-amber-600 dark:text-amber-400 shrink-0">
                        <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white">
                            İzin Türü Kullanımda Uyarısı
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            <strong className="text-slate-800 dark:text-slate-200">{leaveType?.ad}</strong> adlı izin türü aşağıdaki personellerin izin kayıtlarında kullanılmıştır. Pasif hale getirirseniz mevcut kayıtlar korunur ancak yeni talep oluşturulamaz.
                        </p>
                    </div>
                </div>

                <div className="my-4 overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-xl">
                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                        <thead className="bg-slate-50 dark:bg-slate-800/70 sticky top-0">
                            <tr>
                                <th className="px-4 py-2.5 text-left font-semibold text-slate-600 dark:text-slate-300">
                                    Personel Adı Soyadı
                                </th>
                                <th className="px-4 py-2.5 text-left font-semibold text-slate-600 dark:text-slate-300">
                                    Başlangıç Tarihi
                                </th>
                                <th className="px-4 py-2.5 text-left font-semibold text-slate-600 dark:text-slate-300">
                                    Bitiş Tarihi
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {usageDetails.map((detail, index) => (
                                <tr key={index}>
                                    <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-200">
                                        {detail.personelAdiSoyadi || '-'}
                                    </td>
                                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">
                                        {formatDate(detail.baslangicTarihi)}
                                    </td>
                                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">
                                        {formatDate(detail.bitisTarihi)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-end gap-2.5 pt-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                    >
                        İptal
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                    >
                        <Ban className="w-3.5 h-3.5" />
                        Yine de Pasif Yap
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LeaveTypeManagement;
